import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import RawHeader from "../components/RawHeader";
import { InfoFooter } from "./InfoPages";
import { api } from "../lib/api";
import {
  getCart, removeFromCart, updateCartQuantity,
  clearCart, cartSubtotal, subscribeCart,
} from "../lib/cart";
import { preOrderDeposit, preOrderBalance } from "../lib/preOrder";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;
const VAT_RATE = 0.2;

const Cart = () => {
  const [items, setItems] = useState(getCart());
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("raw_guest_email") || ""; } catch { return ""; }
  });
  const [emailSaved, setEmailSaved] = useState(false);

  useEffect(() => subscribeCart(setItems), []);

  const subtotal = cartSubtotal();
  // Pre-order maths — the deposit is what actually gets charged now, and the
  // remaining balance is collected on dispatch. Match backend rule exactly.
  const depositTotal = items.reduce((s, i) => s + preOrderDeposit(i.price) * i.quantity, 0);
  const balanceTotal = items.reduce((s, i) => s + preOrderBalance(i.price) * i.quantity, 0);
  const depositVat = depositTotal * (VAT_RATE / (1 + VAT_RATE));

  // Persist email + best-effort capture for abandoned-cart recovery.
  const saveEmail = async () => {
    const e = (email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email");
      return;
    }
    try { localStorage.setItem("raw_guest_email", e); } catch {}
    try {
      await api.post("/cart/capture-email", {
        email: e,
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.name,
          size: i.size,
          quantity: i.quantity,
          unit_price: i.price,
        })),
        subtotal_gbp: subtotal,
      });
      setEmailSaved(true);
      toast.success("Basket saved — we'll send a reminder if you don't check out");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      // Only silently tolerate network failures — 4xx/5xx should surface
      if (err?.response) {
        toast.error(typeof msg === "string" ? msg : "Could not save basket");
      } else {
        setEmailSaved(true);
      }
    }
  };

  const checkoutAll = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      });
      if (!r?.data?.url) {
        throw new Error("Payment processor did not return a checkout URL.");
      }
      window.location.href = r.data.url;
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = "Checkout failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) msg = `Invalid request: ${raw[0].msg}`;
      else if (e?.message) msg = e.message;
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="min-h-screen text-white">
      <RawHeader variant="public" />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        {new URLSearchParams(window.location.search).get("recovered") === "1" && (
          <div data-testid="recovered-banner" className="mb-8 p-4 border border-[#d4af37] bg-[#d4af37]/10 text-center">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">// WELCOME BACK</div>
            <p className="text-sm text-zinc-300">Your basket is ready. Complete your order below.</p>
          </div>
        )}
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// BASKET</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-10 text-[#d4af37]">Your Basket</h1>

        {items.length === 0 ? (
          <div className="raw-panel p-12 text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-6">BASKET IS EMPTY</div>
            <Link to="/" className="btn-raw btn-raw-primary">→ BROWSE THE SHOP</Link>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {items.map((i) => (
                <div
                  key={i.key}
                  data-testid={`cart-item-${i.product_id}`}
                  className="raw-panel p-4 flex items-center gap-4"
                >
                  <div className="w-20 h-20 bg-black border border-[#d4af37]/30 flex-shrink-0 overflow-hidden">
                    {i.image_base64 ? (
                      <img src={i.image_base64} alt={i.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[9px] text-zinc-600 tracking-[0.2em] uppercase">NO IMG</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${i.product_id}`} className="text-white hover:text-[#d4af37] text-sm sm:text-base truncate block">{i.name}</Link>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">SIZE {i.size}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`dec-${i.product_id}`}
                      onClick={() => updateCartQuantity(i.key, i.quantity - 1)}
                      className="w-8 h-8 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black text-sm font-bold"
                    >−</button>
                    <div className="w-8 text-center font-mono text-white">{i.quantity}</div>
                    <button
                      data-testid={`inc-${i.product_id}`}
                      onClick={() => updateCartQuantity(i.key, i.quantity + 1)}
                      className="w-8 h-8 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black text-sm font-bold"
                    >+</button>
                  </div>
                  <div className="w-32 text-right font-mono text-[10px]">
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-500">Deposit</div>
                    <div className="text-[#d4af37] text-sm">{fmt(preOrderDeposit(i.price) * i.quantity)}</div>
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-600 mt-1">Bal · {fmt(preOrderBalance(i.price) * i.quantity)}</div>
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-600">Full · {fmt(i.price * i.quantity)}</div>
                  </div>
                  <button
                    data-testid={`remove-${i.product_id}`}
                    onClick={() => removeFromCart(i.key)}
                    className="text-zinc-600 hover:text-red-400 text-lg leading-none px-2"
                    aria-label="Remove"
                  >✕</button>
                </div>
              ))}
            </div>

            <div className="raw-panel p-5 mt-6 space-y-2 font-mono text-sm">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// PRE-ORDER SUMMARY</div>
              <div className="flex justify-between text-zinc-500"><span>Full item value</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>Balance due on dispatch</span><span>{fmt(balanceTotal)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>calculated at checkout</span></div>
              <div className="flex justify-between text-zinc-500 text-[10px]"><span>· of which VAT in deposit (20%)</span><span>{fmt(depositVat)}</span></div>
              <div className="flex justify-between text-xl pt-3 border-t-2 border-[#d4af37] mt-3 text-[#d4af37] font-display"><span>PAY NOW · DEPOSIT</span><span>{fmt(depositTotal)}</span></div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 pt-2">
                £{Number(55).toFixed(0)} deposit on items under £1,000 · £{Number(100).toFixed(0)} deposit on £1,000+ items · balance invoiced on dispatch
              </div>
            </div>

            {/* Save basket / abandoned-cart recovery opt-in */}
            <div className="raw-panel p-5 mt-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// SAVE BASKET FOR LATER</div>
              <p className="text-xs text-zinc-400 mb-3">
                Drop your email and we'll remind you if you don't check out. Promo codes apply on the next step.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="cart-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailSaved(false); }}
                  placeholder="you@example.com"
                  className="flex-1 bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none"
                />
                <button
                  data-testid="cart-save-email-btn"
                  type="button"
                  onClick={saveEmail}
                  className={`btn-raw ${emailSaved ? "" : "btn-raw-primary"}`}
                >
                  {emailSaved ? "✓ SAVED" : "SAVE BASKET"}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                data-testid="checkout-btn"
                onClick={checkoutAll}
                disabled={loading}
                className="btn-raw btn-raw-primary flex-1"
              >
                {loading ? "REDIRECTING…" : `→ PRE-ORDER · PAY DEPOSIT ${fmt(depositTotal)}`}
              </button>
              <button
                data-testid="clear-cart-btn"
                onClick={() => { clearCart(); toast.success("Basket cleared"); }}
                className="btn-raw"
              >CLEAR BASKET</button>
              <Link to="/" className="btn-raw">CONTINUE SHOPPING</Link>
            </div>
          </>
        )}
      </section>
      <InfoFooter />
    </div>
  );
};

export default Cart;
